import type { ReactNode } from 'react'
import { At } from './PidDiagram'
import { Pipe, type Cardinal, type ElbowDirection, type PipeStroke } from './Pipe'

/** Absolute lattice coordinate `[col, row]`. */
export type LatticePoint = readonly [number, number]

export interface PipePathProps {
  /**
   * Absolute lattice waypoints. Consecutive points must share a col or a row
   * (axis-aligned). A diagonal hop is auto-expanded horizontal-then-vertical.
   * Place equipment at the same `[col, row]` as path endpoints / vertices.
   */
  points: ReadonlyArray<LatticePoint>
  /** Flow chevron on the first straight segment. */
  flow?: boolean
  color?: string
  stroke?: PipeStroke
  thickness?: number
  className?: string
  title?: string
  /** z-index for segments + elbows (default 1 — keep equipment higher). */
  z?: number
}

/**
 * Connect equipment by listing lattice points — no length / elbow math.
 *
 * ```tsx
 * <PipePath points={[[2, 5], [10, 5], [10, 9], [16, 9]]} flow />
 * <At col={2} row={5} z={3}><Tank … /></At>
 * <At col={16} row={9} z={3}><Tank … /></At>
 * ```
 */
export function PipePath({
  points,
  flow = false,
  color,
  stroke,
  thickness,
  className,
  title,
  z = 1
}: PipePathProps) {
  const path = expandManhattan(points)
  if (path.length < 2) return null

  const parts: ReactNode[] = []

  for (let i = 0; i < path.length - 1; i++) {
    const [c0, r0] = path[i]!
    const [c1, r1] = path[i + 1]!
    const seg = axisSegment(c0, r0, c1, r1)
    if (!seg) continue
    parts.push(
      <At key={`seg-${i}-${c0}-${r0}`} col={c0} row={r0} z={z}>
        <Pipe
          length={seg.length}
          direction={seg.direction}
          flow={flow && i === 0}
          color={color}
          stroke={stroke}
          thickness={thickness}
          className={className}
          title={i === 0 ? title : undefined}
        />
      </At>
    )
  }

  for (let i = 1; i < path.length - 1; i++) {
    const prev = path[i - 1]!
    const cur = path[i]!
    const next = path[i + 1]!
    const inDir = travelDir(prev, cur)
    const outDir = travelDir(cur, next)
    if (!inDir || !outDir || inDir === outDir) continue
    const elbow = elbowFor(inDir, outDir)
    parts.push(
      <At key={`elb-${i}-${cur[0]}-${cur[1]}`} col={cur[0]} row={cur[1]} z={z}>
        <Pipe kind="elbow" direction={elbow} color={color} stroke={stroke} thickness={thickness} />
      </At>
    )
  }

  return parts
}

/** Expand diagonals to an L (horizontal leg first). Drop duplicate points. */
function expandManhattan(points: ReadonlyArray<LatticePoint>): LatticePoint[] {
  const out: LatticePoint[] = []
  for (const p of points) {
    if (!p || p.length < 2) continue
    const next: LatticePoint = [p[0], p[1]]
    if (out.length === 0) {
      out.push(next)
      continue
    }
    const prev = out[out.length - 1]!
    if (prev[0] === next[0] && prev[1] === next[1]) continue
    const dx = next[0] - prev[0]
    const dy = next[1] - prev[1]
    if (dx !== 0 && dy !== 0) {
      out.push([next[0], prev[1]])
    }
    out.push(next)
  }
  return out
}

function axisSegment(
  c0: number,
  r0: number,
  c1: number,
  r1: number
): { length: number; direction: Cardinal } | null {
  const dx = c1 - c0
  const dy = r1 - r0
  if (dx !== 0 && dy !== 0) return null
  if (dx === 0 && dy === 0) return null
  if (dx !== 0) {
    return { length: Math.abs(dx), direction: dx > 0 ? 'right' : 'left' }
  }
  return { length: Math.abs(dy), direction: dy > 0 ? 'down' : 'up' }
}

function travelDir(a: LatticePoint, b: LatticePoint): Cardinal | null {
  return axisSegment(a[0], a[1], b[0], b[1])?.direction ?? null
}

function opposite(d: Cardinal): Cardinal {
  switch (d) {
    case 'right':
      return 'left'
    case 'left':
      return 'right'
    case 'up':
      return 'down'
    case 'down':
      return 'up'
  }
}

function toSide(d: Cardinal): 'top' | 'bottom' | 'left' | 'right' {
  switch (d) {
    case 'up':
      return 'top'
    case 'down':
      return 'bottom'
    case 'left':
      return 'left'
    case 'right':
      return 'right'
  }
}

/** Incoming travel + outgoing travel → named lattice elbow. */
function elbowFor(inDir: Cardinal, outDir: Cardinal): ElbowDirection {
  const enter = toSide(opposite(inDir))
  const exit = toSide(outDir)
  const key = [enter, exit].sort().join('-')
  switch (key) {
    case 'left-top':
      return 'top-left'
    case 'right-top':
      return 'top-right'
    case 'bottom-left':
      return 'bottom-left'
    case 'bottom-right':
      return 'bottom-right'
    default:
      return 'bottom-right'
  }
}
